import { BookOpenIcon, ChevronDown, ChevronRight } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { MessageParts } from '@/components/ui/message-parts';
import { ReasoningSteps } from '@/components/ui/reasoning-steps';
import type { Document } from '@/hooks/useDocuments';
import { cn } from '@/lib/utils';
import { MESSAGE_PROSE_CLASSES } from '@/src/utils/message-rendering-classes';
import type { WebCitationDisplay } from '@/utils/citation-utils';
import { formatElapsedDuration } from '@/utils/date-utils';
import { useSmoothStreamingReveal } from '@/utils/streaming-utils';
import {
  activityChipIsLive,
  finalContinuitySegment,
  type InterleavedTurnItem,
  isFinalContinuitySegment,
  partitionLiveActivities,
  pinLiveActivitiesAboveAnswer,
  shouldCollapseWorkTrail,
  shouldShowFinalAnswerSources,
  shouldShowPendingLeadThinking,
  splitWorkAndFinalAnswer,
  timelineRevealGateIndex,
} from './assistantTurnTimeline';

const REASONING_ICON_CLASS = 'text-[#5F6C96]';
const TIMELINE_ITEM_GAP = 'gap-4.5';

const WorkFinalSeparator: React.FC = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none absolute -top-3 left-0 h-px w-56 max-w-[60%] bg-gradient-to-r from-slate-200 to-transparent"
  />
);

const LeadWaitStatus: React.FC<{
  durationMs?: number;
  waiting: boolean;
}> = ({ durationMs, waiting }) => (
  <div className="flex max-w-full leading-none">
    <div
      className="flex max-w-full items-center gap-1.5 rounded-full pr-1.5 text-left leading-none"
      role="status"
    >
      {waiting && (
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#8FA0D0] crimson-reasoning-pulse"
        />
      )}
      <span className="min-w-0 text-xs font-medium leading-5 text-slate-500">
        {waiting
          ? 'Waiting for agents to finish...'
          : `Waited for ${formatElapsedDuration(durationMs ?? 0)}`}
      </span>
    </div>
  </div>
);


const StreamingTurnText: React.FC<{
  content: string;
  documents?: Document[];
  id: number;
  isStreaming: boolean;
  onRevealChange?: (key: string, revealed: boolean) => void;
  revealKey?: string;
  revealImmediately?: boolean;
  slug: string;
  /** Show the text as found rather than typing it: it is already history.
   *
   * Not optional, and deliberately so. Defaulting this on quietly turned the
   * first paint of live commentary into a snap, and the reveal never ran. That
   * went unnoticed for as long as text arrived a token at a time, because only
   * the first token was swallowed. */
  startWithExistingContent: boolean;
  continuitySegment?: string;
  streamingContinuityKey?: string;
  webCitationMeta?: ReadonlyMap<string, WebCitationDisplay>;
}> = ({
  content,
  continuitySegment,
  documents,
  id,
  isStreaming,
  onRevealChange,
  revealKey,
  revealImmediately = false,
  slug,
  startWithExistingContent,
  streamingContinuityKey,
  webCitationMeta,
}) => {
  const segment = continuitySegment ?? String(id);
  const continuityRoot = isFinalContinuitySegment(continuitySegment)
    ? slug
    : (streamingContinuityKey ?? slug);
  const { content: displayContent, revealed } = useSmoothStreamingReveal(
    content,
    isStreaming,
    false,
    {
      continuityKey: `${continuityRoot}:turn-text:${segment}`,
      startWithExistingContent,
      revealImmediately,
    },
  );

  useLayoutEffect(() => {
    if (!revealKey) return;
    onRevealChange?.(revealKey, revealed);
  }, [onRevealChange, revealKey, revealed]);

  return (
    <div className="cp-turn-text">
      <MessageParts
        className={MESSAGE_PROSE_CLASSES}
        content={displayContent}
        documents={documents}
        flushBottom
        flushTop
        isStreaming={isStreaming}
        slug={slug}
        webCitationMeta={webCitationMeta}
      />
    </div>
  );
};

/** How long the turn has been running, counted from when the turn began.
 *
 * ``turnStartedAt`` is the server's answer to that and is what makes leaving
 * the chat and coming back report the whole run: mounting here would only ever
 * time the visit, and would then be replaced by the true figure on reload.
 *
 * ``freeze`` holds the last figure still: the work trail collapses when the
 * answer is confirmed, and counting past that is just how long this tab sat
 * open after the turn had finished.
 */
export const useStreamingElapsedMs = (
  isStreaming: boolean,
  turnStartedAt: number | null,
  freeze = false,
): number | null => {
  const mountedAtRef = useRef<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);

  useEffect(() => {
    if (!isStreaming) {
      mountedAtRef.current = null;
      setElapsedMs(null);
      return;
    }

    if (mountedAtRef.current == null) {
      mountedAtRef.current = Date.now();
    }
    const startedAt = turnStartedAt ?? mountedAtRef.current;

    if (freeze) {
      setElapsedMs((current) => current ?? Math.max(1, Date.now() - startedAt));
      return;
    }

    const tick = () => setElapsedMs(Math.max(1, Date.now() - startedAt));

    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [freeze, isStreaming, turnStartedAt]);

  return elapsedMs;
};

/** The Worked figure, preferring the server duration over this tab's clock. */
export const resolvedWorkedDurationMs = (
  capturedMs: number | null | undefined,
  liveMs: number | null,
): number | null | undefined => (
  typeof capturedMs === 'number' && Number.isFinite(capturedMs)
    ? capturedMs
    : liveMs
);

// A block of turn text keeps one React key for the life of the turn. Keying it
// by its position would remount it whenever a chip lands above it, and a
// remount mid-reveal tears down the rendered answer and retypes it. Blocks the
// backend has not named fall back to where they sit in the step sequence, which
// only moves for text that has not been written yet.
const streamedTextKey = (
  item: Extract<InterleavedTurnItem, { kind: 'text' }>,
  occurrence: number,
) => `text-${item.messageId ?? `at-${item.id}#${occurrence}`}`;

const TimelineItems: React.FC<{
  documents?: Document[];
  // False when the answer renders below these items, so the last chip here is
  // not the foot of the turn and must not shimmer as if it were.
  endsTheTurn?: boolean;
  isStreaming: boolean;
  items: InterleavedTurnItem[];
  reasoningDurationMs?: number | null;
  revealImmediately?: boolean;
  slug: string;
  streamingContinuityKey?: string;
  catchingUp?: boolean;
  webCitationMeta?: ReadonlyMap<string, WebCitationDisplay>;
}> = ({
  documents,
  endsTheTurn = true,
  isStreaming,
  items,
  reasoningDurationMs,
  revealImmediately = false,
  slug,
  streamingContinuityKey,
  catchingUp = false,
  webCitationMeta,
}) => {
  const [revealedText, setRevealedText] = useState<Record<string, boolean>>({});
  const handleRevealChange = useCallback((key: string, revealed: boolean) => {
    setRevealedText((current) => (
      current[key] === revealed ? current : { ...current, [key]: revealed }
    ));
  }, []);

  // The key each text block reports under, resolved up front so the gate below
  // can be worked out before anything renders.
  const textKeys = useMemo(() => {
    let ordinal = 0;
    return items.map((item) => (
      item.kind === 'text' ? streamedTextKey(item, ordinal++) : null
    ));
  }, [items]);

  const gateIndex = useMemo(() => (
    isStreaming && !catchingUp
      ? timelineRevealGateIndex(
          items,
          (index) => revealedText[textKeys[index] ?? ''] === true,
        )
      : -1
  ), [catchingUp, isStreaming, items, revealedText, textKeys]);

  let assignedDuration = false;
  let finalOrdinal = 0;
  let textOrdinal = 0;

  return (
    <>
      {items.map((item, index) => {
        const gated = gateIndex !== -1 && index > gateIndex;
        // Hold chips *and* later commentary until this block has finished
        // revealing, or a following update appears and the chips then pop in
        // between two blocks the reader has already seen.
        if (gated) return null;
        if (item.kind === 'wait') {
          return (
            <LeadWaitStatus
              key={`wait-${item.id}`}
              durationMs={item.durationMs}
              waiting={item.waiting}
            />
          );
        }
        if (item.kind === 'activity') {
          const duration = assignedDuration ? undefined : reasoningDurationMs;
          assignedDuration = true;
          const isTimelineTail = endsTheTurn && index === items.length - 1;
          // Catch-up must not freeze a thinking-only chip as settled, or coming
          // back mid-thought shows a blank header until the next token. Tool
          // chips still use the catch-up gate so replayed searches don't shimmer.
          const thinkingOnly = !item.steps.some((step) => step.type === 'tool');
          const chipLive = activityChipIsLive(
            item.steps,
            isStreaming,
            isTimelineTail && (!catchingUp || thinkingOnly),
          );
          return (
            <ReasoningSteps
              key={`activity-${item.id}`}
              thinkingSteps={item.steps}
              isStreaming={chipLive}
              isCurrentStep={chipLive && isTimelineTail}
              revealImmediately={revealImmediately}
              defaultExpanded={false}
              reasoningDurationMs={duration}
            />
          );
        }

        const segment = item.isFinal ? finalContinuitySegment(finalOrdinal) : undefined;
        if (item.isFinal) finalOrdinal += 1;
        const key = streamedTextKey(item, textOrdinal);
        textOrdinal += 1;
        return (
          <StreamingTurnText
            key={key}
            content={item.text}
            continuitySegment={segment}
            documents={documents}
            id={item.id}
            isStreaming={isStreaming && !catchingUp}
            onRevealChange={handleRevealChange}
            revealKey={key}
            revealImmediately={revealImmediately}
            slug={slug}
            startWithExistingContent={!isStreaming || catchingUp}
            streamingContinuityKey={streamingContinuityKey}
            webCitationMeta={webCitationMeta}
          />
        );
      })}
    </>
  );
};

const WorkTrailToggle: React.FC<{
  durationMs: number | null | undefined;
  expanded: boolean;
  isStreaming: boolean;
  onToggle: () => void;
}> = ({ durationMs, expanded, isStreaming, onToggle }) => {
  const hasDuration = typeof durationMs === 'number' && Number.isFinite(durationMs);
  const headerLabel = hasDuration
    ? `Worked for ${formatElapsedDuration(durationMs)}`
    : isStreaming
      ? 'Working'
      : 'Worked';

  return (
    <button
      type="button"
      onClick={onToggle}
      className="group flex max-w-full items-center gap-2 rounded-full pr-1.5 text-left leading-none transition-colors hover:bg-slate-50/70"
    >
      <BookOpenIcon className={cn('h-[18px] w-[18px]', REASONING_ICON_CLASS)} />
      <span className="min-w-0 truncate text-xs font-medium leading-5 text-slate-500">
        {headerLabel}
      </span>
      <span className="ml-0.5 shrink-0 text-slate-400 transition-colors group-hover:text-slate-600">
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
      </span>
    </button>
  );
};

interface TurnTimelineProps {
  items: InterleavedTurnItem[];
  isStreaming?: boolean;
  finalAnswerConfirmed?: boolean;
  documents?: Document[];
  slug: string;
  reasoningDurationMs?: number | null;
  /** Epoch ms this turn began, for a turn still running. */
  turnStartedAt?: number | null;
  stopRequested?: boolean;
  streamingContinuityKey?: string;
  catchingUp?: boolean;
  /** Fires when the Sources list is allowed to appear under this turn. */
  onSourcesReady?: (ready: boolean) => void;
  webCitationMeta?: ReadonlyMap<string, WebCitationDisplay>;
}

export const TurnTimeline: React.FC<TurnTimelineProps> = ({
  items,
  isStreaming = false,
  finalAnswerConfirmed = false,
  documents,
  slug,
  reasoningDurationMs,
  turnStartedAt = null,
  stopRequested = false,
  streamingContinuityKey,
  catchingUp = false,
  onSourcesReady,
  webCitationMeta,
}) => {
  const pinnedItems = useMemo(() => pinLiveActivitiesAboveAnswer(items), [items]);
  const finalStreamingStarted = pinnedItems.some(
    (item) => item.kind === 'text' && item.isFinal,
  );
  const finalPhaseStarted = finalAnswerConfirmed || finalStreamingStarted;
  const collapse = shouldCollapseWorkTrail(
    pinnedItems,
    isStreaming,
    finalPhaseStarted,
    stopRequested,
  );
  const { workItems, finalTexts } = splitWorkAndFinalAnswer(pinnedItems, {
    stopped: stopRequested,
    streaming: isStreaming,
  });
  const { settled: settledWorkItems, live: liveWorkItems } = partitionLiveActivities(
    workItems,
    isStreaming,
  );
  const [finalRevealed, setFinalRevealed] = useState<Record<string, boolean>>({});
  const handleFinalRevealChange = useCallback((key: string, revealed: boolean) => {
    setFinalRevealed((current) => (
      current[key] === revealed ? current : { ...current, [key]: revealed }
    ));
  }, []);
  const finalTextKeys = useMemo(
    () => finalTexts.map((item, ordinal) => streamedTextKey(item, ordinal)),
    [finalTexts],
  );
  const sourcesReady = shouldShowFinalAnswerSources({
    finalAnswerRevealed: finalTextKeys.every((key) => finalRevealed[key] === true),
    hasFinalAnswer: finalTexts.length > 0,
    isStreaming,
    stopRequested,
  });
  useLayoutEffect(() => {
    onSourcesReady?.(sourcesReady);
  }, [onSourcesReady, sourcesReady]);
  const liveElapsedMs = useStreamingElapsedMs(
    isStreaming && reasoningDurationMs == null,
    turnStartedAt,
    collapse,
  );
  const [frozenDurationMs, setFrozenDurationMs] = useState<number | null>(null);
  const [workExpanded, setWorkExpanded] = useState(false);
  const showPendingThinking = shouldShowPendingLeadThinking({
    finalAnswerConfirmed: finalPhaseStarted,
    isStreaming,
    // Whatever renders directly above the chip: the collapsed layout hides the
    // work trail behind its toggle and shows only the still-running agents.
    items: collapse ? liveWorkItems : pinnedItems,
    stopRequested,
  });
  useEffect(() => {
    if (!collapse) {
      setFrozenDurationMs(null);
      setWorkExpanded(false);
      return;
    }
    if (isStreaming) {
      setFrozenDurationMs((current) => current ?? liveElapsedMs);
    }
  }, [collapse, isStreaming, liveElapsedMs]);

  const summaryDurationMs = collapse
    ? resolvedWorkedDurationMs(
        reasoningDurationMs,
        isStreaming ? frozenDurationMs ?? liveElapsedMs : reasoningDurationMs ?? null,
      )
    : reasoningDurationMs;

  if (items.length === 0) return null;

  // One tree for both layouts. The work trail changes shape when it collapses,
  // but the answer keeps its slot: giving it a different position per layout
  // would remount it — and it collapses the moment the answer starts arriving,
  // so the remount would land in the middle of the reveal.
  return (
    <div className={`flex min-w-0 flex-col ${collapse ? 'gap-6' : TIMELINE_ITEM_GAP}`}>
      <div className={`flex min-w-0 flex-col${collapse ? '' : ` ${TIMELINE_ITEM_GAP}`}`}>
        {collapse ? (
          <>
            <WorkTrailToggle
              durationMs={summaryDurationMs}
              expanded={workExpanded}
              isStreaming={isStreaming}
              onToggle={() => setWorkExpanded((current) => !current)}
            />
            {workExpanded && settledWorkItems.length > 0 && (
              <div className={`mt-5 flex min-w-0 flex-col ${TIMELINE_ITEM_GAP}`}>
                <TimelineItems
                  documents={documents}
                  isStreaming={false}
                  items={settledWorkItems}
                  slug={slug}
                  streamingContinuityKey={streamingContinuityKey}
                  webCitationMeta={webCitationMeta}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <TimelineItems
              documents={documents}
              endsTheTurn={finalTexts.length === 0}
              isStreaming={isStreaming}
              catchingUp={catchingUp}
              items={workItems}
              reasoningDurationMs={summaryDurationMs}
              revealImmediately={stopRequested}
              slug={slug}
              streamingContinuityKey={streamingContinuityKey}
              webCitationMeta={webCitationMeta}
            />
            {showPendingThinking && (
              <ReasoningSteps
                thinkingSteps={[]}
                isStreaming
                defaultExpanded={false}
              />
            )}
          </>
        )}
      </div>
      {collapse && liveWorkItems.length > 0 && (
        <div className={`flex min-w-0 flex-col ${TIMELINE_ITEM_GAP}`}>
          <TimelineItems
            documents={documents}
            endsTheTurn={finalTexts.length === 0}
            isStreaming={isStreaming}
            catchingUp={catchingUp}
            items={liveWorkItems}
            revealImmediately={stopRequested}
            slug={slug}
            streamingContinuityKey={streamingContinuityKey}
            webCitationMeta={webCitationMeta}
          />
        </div>
      )}
      {collapse && showPendingThinking && (
        <ReasoningSteps
          thinkingSteps={[]}
          isStreaming
          defaultExpanded={false}
        />
      )}
      {finalTexts.length > 0 && (
        <div className="relative flex min-w-0 flex-col gap-3">
          {collapse && <WorkFinalSeparator />}
          {finalTexts.map((finalText, ordinal) => {
            const key = streamedTextKey(finalText, ordinal);
            return (
              <StreamingTurnText
                key={key}
                content={finalText.text}
                continuitySegment={finalContinuitySegment(ordinal)}
                documents={documents}
                id={finalText.id}
                isStreaming={isStreaming && !catchingUp}
                onRevealChange={handleFinalRevealChange}
                revealKey={key}
                revealImmediately={stopRequested}
                slug={slug}
                startWithExistingContent={!isStreaming || catchingUp}
                streamingContinuityKey={streamingContinuityKey}
                webCitationMeta={webCitationMeta}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
